import { useState, useEffect } from 'react';
import { Drawer, Timeline, Typography, Spin, Alert, Tag, Button } from 'antd';
import { getOperationLogListApi } from '@/services/Applog/ApplogService';
import { formatOperationLogDisplay } from '@/utils/operationLogParser';
import SnapshotDiffPanel from '@/components/common/SnapshotDiffPanel';
import './OperationLogDrawer.less';

const { Text } = Typography;

const OperationLogDrawer = ({ open, onClose, entityType, entityId, module, maskClosable = true }) => {
    const [loading, setLoading] = useState(false);
    const [logs, setLogs] = useState([]);
    const [error, setError] = useState(null);

    const [view, setView] = useState('timeline');
    const [currentSnapshot, setCurrentSnapshot] = useState(null);
    const [snapshotTitle, setSnapshotTitle] = useState('');

    useEffect(() => {
        const fetchLogs = async () => {
            if (!entityId || !open) {
                setLogs([]);
                setError(null);
                return;
            }

            setLoading(true);
            setError(null);

            try {
                const params = { entityType, entityId, module, page: 1, size: 100 };
                const res = await getOperationLogListApi(params);

                if (res.status_code === 200) {
                    const logRecords = Array.isArray(res.data) ? res.data : res.data?.records || [];
                    setLogs(logRecords);
                } else {
                    setLogs([]);
                    setError(`API返回状态码: ${res.status_code}`);
                }
            } catch (err) {
                console.error('操作日志抽屉：获取操作日志失败', err);
                setLogs([]);
                setError(`获取操作日志失败: ${err.message}`);
            } finally {
                setLoading(false);
            }
        };

        fetchLogs();
    }, [open, entityType, entityId, module]);

    useEffect(() => {
        if (!open) {
            setView('timeline');
            setCurrentSnapshot(null);
            setSnapshotTitle('');
        }
    }, [open]);

    const formatOperationLog = log => formatOperationLogDisplay(log);

    const getLogDataSnapshot = log => log.dataSnapshot ?? log.data_snapshot;

    const handleSnapshotClick = log => {
        const snapshot = getLogDataSnapshot(log);
        if (!snapshot) return;

        const formattedLog = formatOperationLog(log);
        setCurrentSnapshot(snapshot);
        setSnapshotTitle(formattedLog.description);
        setView('diff');
    };

    const handleBackToTimeline = () => {
        setView('timeline');
        setCurrentSnapshot(null);
        setSnapshotTitle('');
    };

    const handleDrawerClose = () => {
        setView('timeline');
        setCurrentSnapshot(null);
        setSnapshotTitle('');
        onClose();
    };

    const isDiffView = view === 'diff';

    const drawerTitle = isDiffView ? (
        <div className="operation-log-drawer-title">
            <button type="button" className="operation-log-drawer-back-btn" onClick={handleBackToTimeline} aria-label="返回操作记录">
                <i className="bi bi-chevron-left" aria-hidden="true" />
            </button>
            <Text strong>数据变化对比</Text>
            {snapshotTitle ? <Text type="secondary">{snapshotTitle}</Text> : null}
        </div>
    ) : (
        '操作记录'
    );

    return (
        <Drawer
            title={drawerTitle}
            placement="right"
            size="100%"
            onClose={handleDrawerClose}
            open={open}
            closable={{ placement: 'end' }}
            mask={maskClosable ? undefined : { closable: false }}
            push={false}
            className={isDiffView ? 'operation-log-drawer operation-log-drawer--diff' : 'operation-log-drawer'}
            destroyOnHidden
        >
            {isDiffView ? (
                <SnapshotDiffPanel dataSnapshot={currentSnapshot} expanded />
            ) : (
                <div className="operation-log-drawer-timeline">
                    {error && <Alert title="错误信息" description={error} type="error" showIcon style={{ marginBottom: 16 }} />}

                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '40px 0' }}>
                            <Spin size="large" />
                            <div style={{ marginTop: 16 }}>正在加载操作记录...</div>
                        </div>
                    ) : logs.length > 0 ? (
                        <>
                            <div style={{ marginBottom: 16 }}>
                                <Text strong>共 {logs.length} 条操作记录</Text>
                            </div>
                            <Timeline>
                                {logs.map((log, index) => {
                                    const formattedLog = formatOperationLog(log);
                                    return (
                                        <Timeline.Item key={log.id || index} color={formattedLog.result === 'failed' ? 'red' : 'blue'}>
                                            <div>
                                                <p style={{ margin: 0, color: 'var(--ant-color-text-secondary)', fontSize: '12px' }}>{formattedLog.time}</p>
                                                <p style={{ margin: '4px 0 0 0', fontSize: '14px' }}>
                                                    {formattedLog.operator} {formattedLog.description}
                                                    {formattedLog.result === 'failed' && (
                                                        <Tag color="red" style={{ marginLeft: 8 }}>
                                                            失败
                                                        </Tag>
                                                    )}
                                                    {getLogDataSnapshot(log) && (
                                                        <Button type="link" size="small" style={{ marginLeft: 8, padding: '0 4px' }} onClick={() => handleSnapshotClick(log)}>
                                                            查看数据变更
                                                        </Button>
                                                    )}
                                                </p>
                                            </div>
                                        </Timeline.Item>
                                    );
                                })}
                            </Timeline>
                        </>
                    ) : (
                        <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--ant-color-text-secondary)' }}>{error ? '获取数据时发生错误' : '暂无操作记录'}</div>
                    )}
                </div>
            )}
        </Drawer>
    );
};

export default OperationLogDrawer;
